import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { CementStage, Measure } from "../../store/types";
import {
  ExtraCurveRequest,
  makeCurveKey,
} from "../../hooks/use-logChartUnitConversion";
import {
  getChartTitleTranslation,
  getFluidTypeTranslation,
  getUnitTranslation,
} from "../../translations/translations";
import { colorForStageNumber } from "../../utils/stageColors";
import {
  Container,
  TooltipBox,
  Title,
  TableScroll,
  Table,
  Th,
  ThUnit,
  Td,
  Description,
  StageBadge,
  EmptyMessage,
} from "./PumpSchedule.styles";

// Имена кривых активной системы единиц, по которым переводятся величины расписания
// закачки. Совпадают с мнемониками, которые бэкенд знает в /units/.../curve/{name}.
export const PUMP_SCHEDULE_CURVES = {
  volume: "Volume",
  density: "Density",
  rate: "FlowRate",
  time: "eTimPump",
} as const;

// Величина расписания и кривая, к которой она приводится.
const measuresOfStage = (stage: CementStage) => {
  const fluid = stage.cementingFluid;
  const schedule = fluid?.cementPumpSchedule;
  return {
    volume: schedule?.volPump,
    density: fluid?.density,
    rate: schedule?.ratePump,
    time: schedule?.eTimPump,
  };
};

/**
 * Единицы, которые нужно зарегистрировать в useLogChartUnitConversion, чтобы для
 * величин расписания закачки загрузились формулы перевода к активной системе единиц.
 */
export const collectPumpScheduleCurves = (
  stages: CementStage[]
): ExtraCurveRequest[] => {
  const seen = new Set<string>();
  const requests: ExtraCurveRequest[] = [];

  const add = (curveName: string, measure?: Measure) => {
    const unit = measure?.uom?.trim();
    if (!unit) return;
    const key = `${curveName}::${unit}`;
    if (seen.has(key)) return;
    seen.add(key);
    requests.push({ curveName, unit });
  };

  stages.forEach((stage) => {
    const measures = measuresOfStage(stage);
    add(PUMP_SCHEDULE_CURVES.volume, measures.volume);
    add(PUMP_SCHEDULE_CURVES.density, measures.density);
    add(PUMP_SCHEDULE_CURVES.rate, measures.rate);
    add(PUMP_SCHEDULE_CURVES.time, measures.time);
  });

  return requests;
};

const formatNumber = (value: number, precision?: number) => {
  if (!Number.isFinite(value)) return "—";

  const options =
    precision === undefined
      ? { maximumFractionDigits: 6 }
      : { minimumFractionDigits: precision, maximumFractionDigits: precision };

  return new Intl.NumberFormat("ru-RU", options).format(value);
};

type MeasurePicker = (
  measures: ReturnType<typeof measuresOfStage>
) => Measure | undefined;

interface PumpScheduleProps {
  stages: CementStage[];
  // Перевод единичного значения к активной системе единиц (см. useLogChartUnitConversion).
  convertCurveValue: (
    curveName: string,
    sourceUnit: string | undefined,
    value: number
  ) => number;
  // Единица и точность целевой кривой активной системы (ключ — makeCurveKey).
  curveUnits: Record<string, string>;
  curvePrecisions: Record<string, number>;
}

const PumpSchedule: React.FC<PumpScheduleProps> = ({
  stages,
  convertCurveValue,
  curveUnits,
  curvePrecisions,
}) => {
  const { rows, units } = useMemo(() => {
    // Значение стадии, приведённое к активной системе единиц. Единица здесь не нужна —
    // она общая для колонки и выводится под её заголовком.
    const formatValue = (curveName: string, measure?: Measure) => {
      const raw = Number(measure?.value);
      if (!measure || !Number.isFinite(raw)) return null;

      const key = makeCurveKey(curveName);
      const converted = convertCurveValue(curveName, measure.uom, raw);
      return formatNumber(converted, curvePrecisions[key]);
    };

    // Единица колонки: целевая единица активной системы, а пока формула не загрузилась —
    // исходная единица первой заполненной стадии. Обе локализуются словарём.
    const unitOf = (curveName: string, pick: MeasurePicker) => {
      const target = curveUnits[makeCurveKey(curveName)];
      if (target) return getUnitTranslation(target);

      for (const stage of stages) {
        const uom = pick(measuresOfStage(stage))?.uom?.trim();
        if (uom) return getUnitTranslation(uom);
      }
      return "";
    };

    // Первую стадию (numStage === 1) в расписании не показываем, а номера остальных
    // сдвигаем на -1. Сортируем по возрастанию номера стадии.
    const sortedStages = [...stages]
      .filter((stage) => Number(stage.numStage) !== 1)
      .sort((a, b) => Number(a.numStage) - Number(b.numStage));

    return {
      rows: sortedStages.map((stage) => {
        const measures = measuresOfStage(stage);
        const stageNumber = Number(stage.numStage) - 1;
        return {
          uid: stage.uid,
          numStage: stageNumber,
          color: colorForStageNumber(stageNumber),
          descFluid: stage.cementingFluid?.descFluid || "",
          fluidType: stage.cementingFluid?.typeFluid
            ? getFluidTypeTranslation(stage.cementingFluid.typeFluid)
            : "",
          volume: formatValue(PUMP_SCHEDULE_CURVES.volume, measures.volume),
          density: formatValue(PUMP_SCHEDULE_CURVES.density, measures.density),
          rate: formatValue(PUMP_SCHEDULE_CURVES.rate, measures.rate),
          time: formatValue(PUMP_SCHEDULE_CURVES.time, measures.time),
        };
      }),
      units: {
        volume: unitOf(PUMP_SCHEDULE_CURVES.volume, (m) => m.volume),
        density: unitOf(PUMP_SCHEDULE_CURVES.density, (m) => m.density),
        rate: unitOf(PUMP_SCHEDULE_CURVES.rate, (m) => m.rate),
        time: unitOf(PUMP_SCHEDULE_CURVES.time, (m) => m.time),
      },
    };
  }, [stages, convertCurveValue, curveUnits, curvePrecisions]);

  // Тултип типа жидкости ведём на состоянии, а не через primereact с общим
  // селектором: при частых ре-рендерах ViewLog (реалтайм-обновления логов) его
  // привязка к строкам терялась, и тултип срабатывал через раз.
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  const handleRowEnter = (text: string) => (event: React.MouseEvent) => {
    if (!text) return;
    setTooltip({ text, x: event.clientX, y: event.clientY });
  };

  const handleRowMove = (event: React.MouseEvent) => {
    setTooltip((prev) =>
      prev ? { ...prev, x: event.clientX, y: event.clientY } : prev
    );
  };

  const handleRowLeave = () => setTooltip(null);

  return (
    <Container>
      <Title>{getChartTitleTranslation("PumpSchedule")}</Title>
      {rows.length === 0 ? (
        <EmptyMessage>Нет данных о закачке</EmptyMessage>
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>Описание</Th>
                <Th>
                  Объём
                  {units.volume ? <ThUnit>{units.volume}</ThUnit> : null}
                </Th>
                <Th>
                  Плотность
                  {units.density ? <ThUnit>{units.density}</ThUnit> : null}
                </Th>
                <Th>
                  Расход
                  {units.rate ? <ThUnit>{units.rate}</ThUnit> : null}
                </Th>
                <Th>
                  Время
                  {units.time ? <ThUnit>{units.time}</ThUnit> : null}
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.uid}
                  onMouseEnter={handleRowEnter(row.fluidType)}
                  onMouseMove={row.fluidType ? handleRowMove : undefined}
                  onMouseLeave={handleRowLeave}
                >
                  <Td>
                    <Description>
                      <StageBadge $color={row.color}>{row.numStage}</StageBadge>
                      <span>{row.descFluid}</span>
                    </Description>
                  </Td>
                  <Td>{row.volume ?? "—"}</Td>
                  <Td>{row.density ?? "—"}</Td>
                  <Td>{row.rate ?? "—"}</Td>
                  <Td>{row.time ?? "—"}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}
      {tooltip &&
        createPortal(
          <TooltipBox style={{ left: tooltip.x, top: tooltip.y }}>
            {tooltip.text}
          </TooltipBox>,
          document.body
        )}
    </Container>
  );
};

export default PumpSchedule;
